import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Ostatnia deska ratunku, gdy render się wywróci.
 *
 * Bez niej dowolny błąd renderowania daje **białą stronę**. Przy myszy to
 * irytacja — przeładujesz i idziesz dalej. Przy pilocie, dla którego zbudowana
 * jest cała nawigacja przestrzenna, nie ma czym przeładować ani jak zobaczyć,
 * co się stało: telewizor pokazuje pustkę i nie reaguje na nic.
 *
 * Dlatego to jedyny komponent w tym projekcie napisany klasą — React nie daje
 * innego sposobu na przechwycenie błędu renderowania.
 *
 * Świadomie NIE zgłaszamy błędu na serwer. Kanału do tego nie ma, a dorobienie
 * go tutaj znaczyłoby wysyłanie stosu wywołań z przeglądarki do trasy, której
 * nikt nie strzeże. Wystarczy dziennik przeglądarki i możliwość powrotu.
 */
interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Do konsoli, bo tam szuka się przyczyny. `console.error` zamiast loggera:
    // logger mógł być tym, co się właśnie wywróciło.
    console.error("Błąd renderowania:", error, info.componentStack);
  }

  /**
   * Fokus na przycisku powrotu, gdy ekran ratunkowy się pojawi.
   *
   * Jawne wywołanie, a NIE atrybut `autoFocus` — i to nie jest omijanie reguły
   * dostępności, tylko inna czynność. `autoFocus` przestawia fokus przy każdym
   * wejściu na stronę i dlatego jest odradzany; tutaj ekran pojawia się wyłącznie
   * po awarii i bez fokusu byłby dla pilota ślepym zaułkiem: strzałki nie mają od
   * czego zacząć, więc widz na telewizorze nie sięgnie żadnego przycisku.
   *
   * Ref wywołaniem, bo w komponencie klasowym to najkrótsza droga do „zrób coś,
   * gdy element się podłączy", bez dokładania cyklu życia.
   */
  private readonly focusOnAppear = (node: HTMLButtonElement | null): void => {
    node?.focus();
  };

  private readonly reset = (): void => {
    /**
     * Pełne przeładowanie, nie samo wyzerowanie stanu.
     *
     * Po wyjątku w renderze stan aplikacji jest niewiadomy — wyzerowanie
     * granicy wróciłoby do tego samego drzewa i najczęściej do tego samego
     * błędu, tyle że bez komunikatu. Przeładowanie jest brzydsze i uczciwsze.
     */
    window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-8 text-center">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold">Coś się posypało</h1>
          <p className="max-w-md text-muted-foreground">
            Ekran nie dał się narysować. To błąd po naszej stronie — dane są bezpieczne, a
            odświeżenie zwykle wystarcza.
          </p>
        </div>

        {/*
          Przyciski to zwykłe `<button>`, a nie nasz primityw, i to jest celowe:
          granica ma działać także wtedy, gdy wywrócił się właśnie system
          komponentów. Im mniej ta gałąź importuje, tym większa szansa, że
          w ogóle się narysuje.

          Fokus ustawiamy jawnie (patrz `focusOnAppear`) — pilot nie ma czym
          wskazać przycisku, jeśli nic go nie trzyma.
        */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            ref={this.focusOnAppear}
            onClick={this.reset}
            className="rounded-lg bg-primary px-5 py-2.5 font-medium text-primary-foreground"
          >
            Odśwież stronę
          </button>
          <a
            href="/"
            className="rounded-lg border border-border px-5 py-2.5 font-medium text-foreground"
          >
            Wróć na stronę główną
          </a>
        </div>

        {/*
          Treść błędu pod spodem, nie w oknie dialogowym: na telewizorze nie ma
          jak jej skopiować, więc ma być widoczna od razu i dać się przeczytać
          z trzech metrów. Sam stos pomijamy — jest w konsoli, a tutaj byłby
          ścianą tekstu bez wartości dla patrzącego.
        */}
        <p className="max-w-lg font-mono text-xs break-words text-muted-foreground">
          {error.message}
        </p>
      </div>
    );
  }
}
